// Rebuy smart search - overlay + /search page. Cards are rendered by Shopify (rebuy-search-grid section).
// helpers from rebuy-shared.js (theme.liquid loads it in <head>, before this file)
if (!window.RebuyShared) throw new Error('[rebuy-search] rebuy-shared.js must load before rebuy-search.js');
const { getApiKey, renderCards, renderSkeletons } = window.RebuyShared;

const REBUY_API = 'https://rebuyengine.com/api/v1/smart-search';
const OVERLAY_PRODUCTS = 3;
const DEBOUNCE_MS = 250;

// config printed by snippets/rebuy-search-config.liquid
const readConfig = () => {
  const el = document.getElementById('rebuy-search-config');
  if (!el) return null;
  try {
    return JSON.parse(el.textContent);
  } catch (error) {
    console.warn('[rebuy-search] invalid config', error);
    return null;
  }
};

const config = readConfig();
const root = ((config && config.root) || '/').replace(/\/$/, '');
const searchUrl = (config && config.searchUrl) || '/search';
const strings = (config && config.strings) || {};

const debounce = (fn, wait) => {
  let timer;
  return (...args) => {
    clearTimeout(timer);
    timer = setTimeout(() => fn(...args), wait);
  };
};

const searchHref = (term) => `${searchUrl}?q=${encodeURIComponent(term)}`;

const rebuyPost = async (path, body) => {
  const response = await fetch(`${REBUY_API}${path}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Rebuy-Api-Key': await getApiKey(),
    },
    body: JSON.stringify(body),
  });
  if (!response.ok) throw new Error(`Rebuy ${path} ${response.status}`);
  const json = await response.json();
  return { json, headers: response.headers };
};

// used while typing in the overlay
const rebuyQuickView = async (query) => {
  const { json } = await rebuyPost('/search/quick-view', { query });
  const data = (json && json.data) || {};
  return {
    products: data.productSuggestions || [],
    collections: data.collections || [],
  };
};

// used on the results page
const rebuySearch = async ({ searchTerm = '', page = 1, pageSize = 24, sortBy = null, filters = {}, aggregate = false }) => {
  // only need the handle to render the card
  const body = { searchTerm, currentPage: page, pageSize, fields: ['id', 'handle', 'name', 'url'] };
  if (sortBy) body.sortBy = sortBy;
  if (Object.keys(filters).length) body.filters = filters;
  // TODO: add 'options' (kleur/maat) once Rebuy shares the filters.options format
  if (aggregate) body.aggregate = { fields: ['categories', 'availability'] };

  const { json, headers } = await rebuyPost('/search', body);
  const data = (json && json.data) || {};
  const products = Array.isArray(data) ? data : data.products || [];
  const pageInfo = data.pageInfo || {};
  const headerTotal = parseInt(headers.get('Search-Size-Total'), 10);
  const total = Number.isFinite(headerTotal) ? headerTotal : parseInt(pageInfo.serverSize, 10) || products.length;

  return { products, total, aggregates: data.aggregates || [] };
};

// backup products when suggested products are off / not available in this market
const rebuyBestSellers = async (limit) => {
  const params = new URLSearchParams({ key: await getApiKey(), limit, filter_oos: 'yes' });
  const response = await fetch(`https://rebuyengine.com/api/v1/products/top_sellers?${params}`);
  if (!response.ok) throw new Error(`Rebuy top_sellers ${response.status}`);
  const json = await response.json();
  return (json && json.data) || [];
};

const EMPTY_SUGGESTIONS = { terms: [], collections: [], products: [], titles: {}, autoComplete: { enabled: true, max: 6 } };
let settingsPromise;

// Rebuy admin > Smart Search > Quick View settings (endpoint not in their public docs)
const getQuickViewSettings = () => {
  if (!settingsPromise) {
    settingsPromise = (async () => {
      const response = await fetch(`${REBUY_API}/settings`, { headers: { 'Rebuy-Api-Key': await getApiKey() } });
      if (!response.ok) throw new Error(`Rebuy settings ${response.status}`);
      const json = await response.json();
      const quickView = json && json.data && json.data.quickViewSettings;
      return (quickView && quickView.setting) || null;
    })().catch((error) => {
      console.warn('[rebuy-search] Quick View settings unavailable', error);
      return null;
    });
  }
  return settingsPromise;
};

// popular searches, categories, suggested products from Rebuy (section titles stay the theme's own)
const getRebuySuggestions = async ({ noResults = false } = {}) => {
  const setting = await getQuickViewSettings();
  if (!setting) return EMPTY_SUGGESTIONS;

  const layout = setting.editorLayout || {};
  const list = (value) => (Array.isArray(value) ? value : []);
  const isOn = (item) => item && item.enabled !== false && item.enabled !== 0 && item.visible !== false;
  const byIndex = (a, b) => (a.index || 0) - (b.index || 0);
  const pick = (key, enabledKey, noResultsEnabledKey) => {
    const alternative = list(setting.noResults && setting.noResults[key]);
    if (noResults && layout[noResultsEnabledKey] && alternative.length) return alternative;
    return layout[enabledKey] === false ? [] : list(setting[key]);
  };

  const terms = pick('suggestedSearchTerms', 'suggestedSearchTermsEnabled', 'noResultsSuggestedTermsEnabled')
    .filter((term) => typeof term === 'string' || isOn(term))
    .sort(byIndex)
    .map((term) => (typeof term === 'string' ? term : term.term))
    .filter(Boolean);

  const collections = pick('suggestedCollections', 'suggestedCollectionsEnabled', 'noResultsSuggestedCollectionsEnabled')
    .filter(isOn)
    .sort(byIndex)
    .filter((collection) => collection.handle && (collection.name || collection.title))
    .map((collection) => ({
      label: collection.name || collection.title,
      href: `${root}/collections/${collection.handle}`,
    }));

  const products = pick('suggestedProducts', 'suggestedProductsEnabled', 'noResultsSuggestedProductsEnabled');

  return {
    terms,
    collections,
    products,
    autoComplete: {
      enabled: layout.autoCompleteTermsEnabled !== false,
      title: layout.autoCompleteTermsTitle,
      max: parseInt(layout.autoCompleteTermsMax, 10) || 6,
    },
  };
};

const fillLinks = (list, items) => {
  list.innerHTML = '';
  items.forEach(({ label, href }) => {
    const li = document.createElement('li');
    const link = document.createElement('a');
    link.href = href;
    link.textContent = label;
    li.appendChild(link);
    list.appendChild(li);
  });
};

// titles come from the theme locales (rebuy-search-lists.liquid), only the items come from rebuy
const renderSuggestionLists = async (scope, options) => {
  const { terms, collections } = await getRebuySuggestions(options);
  const blocks = [
    ['terms', terms.map((term) => ({ label: term, href: searchHref(term) }))],
    ['collections', collections],
  ];
  blocks.forEach(([key, items]) => {
    const block = scope.querySelector(`[data-rs-${key}-block]`);
    if (!block) return;
    fillLinks(block.querySelector(`[data-rs-${key}]`), items);
    block.hidden = !items.length;
  });
};

// rebuy picks first, top sellers fill the gaps
const renderSuggestedProducts = async (container, limit, options) => {
  renderSkeletons(container, limit);
  const { products } = await getRebuySuggestions(options);
  try {
    const topSellers = await rebuyBestSellers(limit + 4).catch(() => []);
    await renderCards(container, [...products, ...topSellers], { limit });
  } catch (error) {
    console.warn('[rebuy-search] suggested products failed', error);
    container.innerHTML = '';
  }
};

const FOCUSABLE = 'a[href], button:not([disabled]), input:not([type="hidden"]), select, textarea, [tabindex]:not([tabindex="-1"])';

// keep Tab / Shift+Tab inside an open dialog (it's aria-modal, so nothing outside is reachable for AT)
const trapFocus = (container, event) => {
  const items = [...container.querySelectorAll(FOCUSABLE)].filter((el) => el.offsetParent !== null);
  if (!items.length) return;
  const first = items[0];
  const last = items[items.length - 1];
  if (!container.contains(document.activeElement)) {
    event.preventDefault();
    first.focus();
  } else if (event.shiftKey && document.activeElement === first) {
    event.preventDefault();
    last.focus();
  } else if (!event.shiftKey && document.activeElement === last) {
    event.preventDefault();
    first.focus();
  }
};

// header search overlay
class RebuySearchOverlay extends HTMLElement {
  connectedCallback() {
    if (!config) return;

    this.panel = this.querySelector('.rs-overlay__panel');
    this.input = this.querySelector('[data-rs-input]');
    this.form = this.querySelector('[data-rs-form]');
    this.clearButton = this.querySelector('[data-rs-clear]');
    this.defaultState = this.querySelector('[data-rs-state="default"]');
    this.queryState = this.querySelector('[data-rs-state="query"]');
    this.emptyMessage = this.querySelector('[data-rs-empty-message]');
    this.bestSellers = this.querySelector('[data-rs-bestsellers]');
    this.productsGrid = this.querySelector('[data-rs-products]');
    this.suggestions = this.querySelector('[data-rs-suggestions]');
    this.suggestionsTitle = this.querySelector('[data-rs-suggestions-title]');
    this.autoComplete = EMPTY_SUGGESTIONS.autoComplete;
    this.seeAll = this.querySelector('[data-rs-see-all]');
    this.seeAllLabel = this.querySelector('[data-rs-see-all-label]');
    this.requestId = 0;
    this.bestSellersLoaded = false;

    this.querySelectorAll('[data-rs-close]').forEach((el) => el.addEventListener('click', () => this.close()));
    this.clearButton.addEventListener('click', () => {
      this.input.value = '';
      this.onInput();
      this.input.focus();
    });
    this.input.addEventListener('input', () => this.onInput());
    this.form.addEventListener('submit', (event) => {
      if (!this.input.value.trim()) event.preventDefault();
    });

    // on document while open, so Escape still works if focus somehow left the panel
    this.onKeydown = (event) => {
      if (event.key === 'Escape') this.close();
      else if (event.key === 'Tab') trapFocus(this.panel, event);
    };

    // header height changes on rotate / resize (announcement bar wraps), keep the overlay right under it
    this.onResize = debounce(() => this.positionUnderHeader(), 100);

    this.search = debounce((query) => this.runSearch(query), DEBOUNCE_MS);
    this.bindTriggers();
  }

  positionUnderHeader() {
    const header = document.querySelector('.header');
    const top = header ? Math.max(0, Math.round(header.getBoundingClientRect().bottom)) : 0;
    this.style.setProperty('--rs-top', `${top}px`);
  }

  // the old predictive search modal isn't rendered when rebuy search is on (predictive-search-modal.liquid),
  // so a normal click listener is enough here
  bindTriggers() {
    document.addEventListener('click', (event) => {
      const trigger = event.target.closest('.search-trigger');
      if (!trigger) return;
      event.preventDefault();
      if (this.hidden) this.open('', trigger);
      else this.close();
    });

    // search field in the mobile menu forwards to the overlay
    document.addEventListener('focusin', (event) => {
      const field = event.target;
      if (!field.matches('#input-search-mobile')) return;
      field.blur();
      this.open(field.value, null);
    });
  }

  // open below the header
  open(value = '', opener = document.activeElement) {
    if (!this.hidden) return;
    this.opener = opener;
    this.positionUnderHeader();
    this.hidden = false;
    document.body.classList.add('js-scroll-lock', 'rs-overlay-open');
    document.addEventListener('keydown', this.onKeydown);
    window.addEventListener('resize', this.onResize);
    window.addEventListener('orientationchange', this.onResize);
    if (value) this.input.value = value;
    this.onInput();
    // focus in the same tick as the tap, iOS only shows the keyboard for focus inside the user gesture
    this.input.focus({ preventScroll: true });

    // load lists + best sellers only on first open
    if (!this.bestSellersLoaded) {
      this.bestSellersLoaded = true;
      renderSuggestionLists(this);
      renderSuggestedProducts(this.bestSellers, OVERLAY_PRODUCTS);
      getRebuySuggestions().then(({ terms, autoComplete }) => {
        this.popularTerms = terms;
        this.autoComplete = autoComplete;
        if (autoComplete.title && this.suggestionsTitle) this.suggestionsTitle.textContent = autoComplete.title;
      });
    }
  }

  close() {
    if (this.hidden) return;
    this.hidden = true;
    document.body.classList.remove('js-scroll-lock', 'rs-overlay-open');
    document.removeEventListener('keydown', this.onKeydown);
    window.removeEventListener('resize', this.onResize);
    window.removeEventListener('orientationchange', this.onResize);
    // back to the search icon, so keyboard users don't land at the top of the page
    if (this.opener && this.opener.isConnected) this.opener.focus();
    this.opener = null;
  }

  onInput() {
    const query = this.input.value.trim();
    this.clearButton.hidden = !this.input.value;
    if (!query) {
      this.requestId += 1;
      this.showState('default');
      return;
    }
    this.search(query);
  }

  // default | query | empty (no results) | error (rebuy unreachable)
  showState(state, query = '') {
    this.defaultState.hidden = state === 'query';
    this.queryState.hidden = state !== 'query';
    this.emptyMessage.hidden = state !== 'empty' && state !== 'error';
    this.emptyMessage.textContent = '';

    if (state === 'empty') {
      this.emptyMessage.textContent = `${strings.noResultsTitle} "${query}". ${strings.noResultsText}`;
    }
    if (state === 'error') {
      // don't say "no results" when we just couldn't ask; send them to the normal shopify search instead
      const link = document.createElement('a');
      link.href = searchHref(query);
      link.textContent = `${strings.seeAll} "${query}"`;
      this.emptyMessage.append(`${strings.unavailable} `, link);
    }
  }

  async runSearch(query) {
    const requestId = ++this.requestId;
    this.classList.add('is-loading');

    let result = null;
    try {
      result = await rebuyQuickView(query);
    } catch (error) {
      try {
        const { products } = await rebuySearch({ searchTerm: query, pageSize: OVERLAY_PRODUCTS + 4 });
        result = { products, collections: [] };
      } catch (fallbackError) {
        console.warn('[rebuy-search] search failed', fallbackError);
      }
    }
    // ignore old responses
    if (requestId !== this.requestId) return;
    this.classList.remove('is-loading');

    if (!result) {
      this.showState('error', query);
      return;
    }

    const products = result.products;
    if (!products.length) {
      this.showState('empty', query);
      return;
    }

    this.renderSuggestions(query, result);
    this.seeAll.href = searchHref(query);
    this.seeAllLabel.textContent = `${strings.seeAll} "${query}"`;
    this.showState('query');
    renderCards(this.productsGrid, products, { limit: OVERLAY_PRODUCTS });
  }

  renderSuggestions(query, { products, collections }) {
    const lower = query.toLowerCase();
    const seen = new Set();
    const items = [];
    const add = (label, href) => {
      const key = label.toLowerCase().trim();
      if (!key || seen.has(key) || items.length >= this.autoComplete.max) return;
      seen.add(key);
      items.push({ label: label.trim(), href });
    };

    add(query, searchHref(query));
    (this.popularTerms || []).filter((term) => term.toLowerCase().includes(lower)).forEach((term) => add(term, searchHref(term)));
    collections.forEach((collection) => {
      const title = collection.title || collection.name;
      if (!title || !title.toLowerCase().includes(lower)) return;
      // build the link ourselves instead of trusting a url from the API
      const href = collection.handle ? `${root}/collections/${encodeURIComponent(collection.handle)}` : searchHref(title);
      add(title, href);
    });
    products.forEach((product) => {
      const name = String(product.name || product.title || '').split(' - ')[0];
      if (name) add(name, searchHref(name));
    });

    this.suggestions.closest('.rs-block').hidden = !this.autoComplete.enabled;
    this.suggestions.innerHTML = '';
    items.forEach(({ label, href }) => {
      const li = document.createElement('li');
      const link = document.createElement('a');
      link.href = href;
      link.textContent = label;
      li.appendChild(link);
      this.suggestions.appendChild(li);
    });
  }
}

const AGGREGATE_LABEL_KEYS = ['value', 'name', 'label'];

// rebuy aggregates -> [{ field, group, values }] for the filter drawer
const parseAggregates = (aggregates) => {
  const groups = [];
  const list = Array.isArray(aggregates) ? aggregates : Object.entries(aggregates || {}).map(([k, v]) => ({ [k]: v }));

  list.forEach((aggregate) => {
    Object.entries(aggregate || {}).forEach(([field, entries]) => {
      if (field === 'price' || field === 'availability' || !Array.isArray(entries)) return;
      entries.forEach((entry) => {
        let value = typeof entry === 'string' ? entry : entry[field];
        if (value === undefined) value = AGGREGATE_LABEL_KEYS.map((key) => entry[key]).find((v) => typeof v === 'string');
        if (typeof value !== 'string' || !value) return;
        const count = parseInt(entry.numProducts ?? entry.count, 10);

        let group = field === 'categories' ? strings.categories || 'Category' : field;
        let label = value;
        if (field === 'options' && value.includes(':')) {
          [group, label] = [value.slice(0, value.indexOf(':')), value.slice(value.indexOf(':') + 1)];
        }

        let target = groups.find((g) => g.field === field && g.group === group);
        if (!target) {
          target = { field, group, values: [] };
          groups.push(target);
        }
        target.values.push({ value, label, count: Number.isFinite(count) ? count : null });
      });
    });
  });
  return groups;
};

// /search results page
class RebuySearchPage extends HTMLElement {
  connectedCallback() {
    if (!config) return;

    this.titleEl = this.querySelector('[data-rs-page-title]');
    this.results = this.querySelector('[data-rs-page-results]');
    this.grid = this.querySelector('[data-rs-page-grid]');
    this.empty = this.querySelector('[data-rs-page-empty]');
    this.popularPieces = this.querySelector('[data-rs-popular-pieces]');
    this.sortSelect = this.querySelector('[data-rs-sort]');
    this.loadMore = this.querySelector('[data-rs-loadmore]');
    this.loadMoreButton = this.querySelector('[data-rs-loadmore-button]');
    this.loadMoreText = this.querySelector('[data-rs-loadmore-text]');
    this.loadMoreProgress = this.querySelector('[data-rs-loadmore-progress]');
    this.drawer = this.querySelector('[data-rs-drawer]');
    this.facets = this.querySelector('[data-rs-facets]');
    this.filterLabel = this.querySelector('[data-rs-filter-label]');
    this.subtitle = this.querySelector('[data-rs-page-subtitle]');
    this.activeFilters = this.querySelector('[data-rs-active-filters]');
    this.loading = this.querySelector('[data-rs-loading]');
    this.errorMessage = this.querySelector('[data-rs-page-error]');
    this.filterButton = this.querySelector('[data-rs-filter-open]');
    // native shopify results (main-search.liquid), shown until rebuy answers and kept if it doesn't
    this.native = document.querySelector('[data-rs-native]');
    this.ready = false;

    const params = new URLSearchParams(window.location.search);
    this.state = {
      query: (params.get('q') || this.dataset.initialQuery || '').trim(),
      sort: params.get('sort_by') || '',
      selected: params.getAll('filter').filter(Boolean),
      inStock: params.get('in_stock') === '1',
      page: 1,
      shown: 0,
      total: 0,
    };
    this.pageSize = parseInt(config.pageSize, 10) || 24;
    this.groups = [];
    this.requestId = 0;

    this.sortSelect.value = this.state.sort;
    this.sortSelect.addEventListener('change', () => {
      this.state.sort = this.sortSelect.value;
      this.run();
    });
    this.loadMoreButton.addEventListener('click', () => this.run({ append: true }));
    this.filterButton.addEventListener('click', () => this.toggleDrawer(true));
    this.querySelectorAll('[data-rs-filter-close]').forEach((el) => el.addEventListener('click', () => this.toggleDrawer(false)));
    this.querySelector('[data-rs-filter-clear]').addEventListener('click', () => {
      this.state.selected = [];
      this.state.inStock = false;
      this.run();
    });
    this.onDrawerKeydown = (event) => {
      if (event.key === 'Escape') this.toggleDrawer(false);
      else if (event.key === 'Tab') trapFocus(this.drawer, event);
    };

    this.run();
  }

  toggleDrawer(open) {
    if (this.drawer.hidden === !open) return;
    this.drawer.hidden = !open;
    document.body.classList.toggle('js-scroll-lock', open);
    if (open) {
      document.addEventListener('keydown', this.onDrawerKeydown);
      this.drawer.querySelector('[data-rs-filter-close]').focus();
    } else {
      document.removeEventListener('keydown', this.onDrawerKeydown);
      this.filterButton.focus();
    }
  }

  // first rebuy answer: drop the native results and show ours.
  // if the 12s fallback already showed the native results, keep them - don't swap the page under the shopper
  markReady() {
    if (this.ready) return true;
    if (document.documentElement.classList.contains('rs-fallback')) {
      this.fallbackToNative();
      return false;
    }
    this.ready = true;
    if (this.native) this.native.remove();
    document.documentElement.classList.remove('rs-pending');
    return true;
  }

  // rebuy unreachable before anything rendered: keep the normal shopify search results
  fallbackToNative() {
    this.hidden = true;
    document.documentElement.classList.remove('rs-pending');
  }

  buildFilters() {
    const filters = {};
    this.state.selected.forEach((key) => {
      const [field, ...rest] = key.split('|');
      const value = rest.join('|');
      if (!field || !value) return;
      (filters[field] = filters[field] || []).push(value);
    });
    if (this.state.inStock) filters.available = true;
    return filters;
  }

  // keep sort/filters in the url so refresh keeps them
  syncUrl() {
    const params = new URLSearchParams();
    params.set('q', this.state.query);
    params.set('type', 'product');
    if (this.state.sort) params.set('sort_by', this.state.sort);
    this.state.selected.forEach((key) => params.append('filter', key));
    if (this.state.inStock) params.set('in_stock', '1');
    window.history.replaceState({}, '', `${window.location.pathname}?${params}`);
  }

  async run({ append = false } = {}) {
    const requestId = ++this.requestId;
    const page = append ? this.state.page + 1 : 1;
    const [field, order] = this.state.sort.split(':');
    const hasFilters = this.state.selected.length > 0 || this.state.inStock;

    this.loading.hidden = false;
    if (!append) this.syncUrl();

    let response;
    try {
      response = this.state.query
        ? await rebuySearch({
            searchTerm: this.state.query,
            page,
            pageSize: this.pageSize,
            sortBy: field ? { field, order } : null,
            filters: this.buildFilters(),
            aggregate: !append,
          })
        : { products: [], total: 0, aggregates: [] };
    } catch (error) {
      console.warn('[rebuy-search] results failed', error);
    }
    if (requestId !== this.requestId) return;
    this.loading.hidden = true;

    // failed: never show "no results" for it. first load -> shopify results, later (sort/filter/more) -> keep grid + message
    if (!response) {
      if (!this.ready) this.fallbackToNative();
      else this.errorMessage.hidden = false;
      return;
    }
    this.errorMessage.hidden = true;
    if (!this.markReady()) return;

    this.state.page = page;
    this.state.total = response.total;

    if (!append) {
      // keep the unfiltered groups, otherwise options disappear after selecting one
      const groups = parseAggregates(response.aggregates);
      if (!hasFilters || !this.groups.length) this.groups = groups;
      this.renderFacets();
      this.renderActiveFilters();
    }

    if (!response.total && !hasFilters) {
      this.showNoResults();
      return;
    }

    this.empty.hidden = true;
    this.results.hidden = false;
    this.renderTitle(response.total);

    await renderCards(this.grid, response.products, { append });
    if (requestId !== this.requestId) return;
    this.state.shown = this.grid.querySelectorAll('.product-button, .rs-card-skeleton').length;
    this.renderLoadMore(response.products.length);
  }

  renderTitle(total) {
    const label = total === 1 ? strings.resultFor : strings.resultsFor;
    this.titleEl.textContent = `${total} ${label} "${this.state.query}"`;
    this.subtitle.hidden = true;
    this.filterLabel.textContent = (strings.filterCount || strings.filter || 'Filter').replace('[count]', total);
  }

  renderLoadMore(lastPageCount) {
    const shown = Math.min(this.state.page * this.pageSize, this.state.total);
    const hasMore = shown < this.state.total && lastPageCount >= this.pageSize;
    this.loadMore.hidden = !this.state.total;
    this.loadMoreButton.hidden = !hasMore;
    this.loadMoreProgress.max = this.state.total || 1;
    this.loadMoreProgress.value = shown;
    this.loadMoreText.textContent = (strings.showing || '')
      .replace('[shown]', shown)
      .replace('[total]', this.state.total);
  }

  showNoResults() {
    this.results.hidden = true;
    this.empty.hidden = false;
    this.titleEl.textContent = this.state.query ? `${strings.noResultsTitle} "${this.state.query}"` : '';
    this.subtitle.hidden = !this.state.query;
    if (!this.popularPiecesLoaded) {
      this.popularPiecesLoaded = true;
      renderSuggestionLists(this.empty, { noResults: true });
      renderSuggestedProducts(this.popularPieces, 3, { noResults: true });
    }
  }

  renderFacets() {
    this.facets.innerHTML = '';

    const stock = document.createElement('label');
    stock.className = 'rs-facet__option rs-facet__option--stock';
    stock.innerHTML = '<input type="checkbox"><span></span>';
    stock.querySelector('span').textContent = strings.inStock || 'In stock';
    const stockInput = stock.querySelector('input');
    stockInput.checked = this.state.inStock;
    stockInput.addEventListener('change', () => {
      this.state.inStock = stockInput.checked;
      this.run();
    });
    this.facets.appendChild(stock);

    this.groups.forEach((group, index) => {
      const details = document.createElement('details');
      details.className = 'rs-facet';
      details.open = index < 2 || group.values.some((v) => this.state.selected.includes(`${group.field}|${v.value}`));

      const summary = document.createElement('summary');
      summary.className = 'rs-facet__title';
      summary.textContent = group.group;
      details.appendChild(summary);

      const list = document.createElement('div');
      list.className = 'rs-facet__list';
      group.values.forEach((entry) => {
        const key = `${group.field}|${entry.value}`;
        const option = document.createElement('label');
        option.className = 'rs-facet__option';
        option.innerHTML = '<input type="checkbox"><span></span><small></small>';
        option.querySelector('span').textContent = entry.label;
        if (entry.count !== null) option.querySelector('small').textContent = `(${entry.count})`;
        const input = option.querySelector('input');
        input.checked = this.state.selected.includes(key);
        input.addEventListener('change', () => {
          this.state.selected = input.checked
            ? [...this.state.selected, key]
            : this.state.selected.filter((item) => item !== key);
          this.run();
        });
        list.appendChild(option);
      });
      details.appendChild(list);
      this.facets.appendChild(details);
    });
  }

  renderActiveFilters() {
    this.activeFilters.innerHTML = '';
    const addTag = (label, onRemove) => {
      const tag = document.createElement('button');
      tag.type = 'button';
      tag.className = 'rs-active-filters__tag';
      tag.textContent = label;
      tag.addEventListener('click', onRemove);
      this.activeFilters.appendChild(tag);
    };
    if (this.state.inStock) {
      addTag(strings.inStock, () => {
        this.state.inStock = false;
        this.run();
      });
    }
    this.state.selected.forEach((key) => {
      const value = key.slice(key.indexOf('|') + 1);
      const label = value.includes(':') ? value.slice(value.indexOf(':') + 1) : value;
      addTag(label, () => {
        this.state.selected = this.state.selected.filter((item) => item !== key);
        this.run();
      });
    });
  }
}

if (!customElements.get('rebuy-search-overlay')) {
  customElements.define('rebuy-search-overlay', RebuySearchOverlay);
}
if (!customElements.get('rebuy-search-page')) {
  customElements.define('rebuy-search-page', RebuySearchPage);
}
